import * as THREE from "three";
import { MATS } from "../materials.js";
import { GRADE } from "../spec.js";
import * as H from "./spec.js";

/**
 * The hero house masonry and roofs: four brick walls built AROUND the
 * H.OPENINGS rectangles (each left as a ~0.10 m recess for openings.js to
 * fill), the main hip roof, the front gable dormer and entry gable with their
 * sunburst tympana, the porch shed roof, the bay-window box and cap, gutters,
 * downspouts, a dish and roof vents.
 *
 * Collision strategy: every visual piece is solid:false; instead each wall
 * gets a coarse box collider per solid run (so the recesses never become
 * cat-sized gaps, while the two doorways stay open),
 * and the climbable surfaces -- porch roof, bay cap, the hip itself -- get
 * flat colliders at their real heights.  The hip is approximated by stacked
 * terraces 0.50 m apart (cat jumps 0.95 m, so the roof is climbable to the
 * ridge without ever floating more than ~0.25 m off the visual slope).
 */

const B = MATS[H.LOOK.brick];
const TR = MATS[H.LOOK.trim];
const TS = MATS[H.LOOK.trimShade];
const RF = MATS[H.LOOK.roof];
const ST = MATS[H.LOOK.stone];
const GL = MATS[H.LOOK.glass];
const MD = MATS.metalDark;
const MP = MATS.metalPale;

const WT = H.WALL_T;    // wall thickness
const RECESS = 0.11;    // opening recess depth: openings.js glazing sits ~FACE-0.10
const OV = 0.45;        // main roof eave overhang
const FLAT = { solid: false, ao: 0.05, tint: 0.05 }; // wall fragments: near-flat
// shading, or the per-piece bottom-darkening makes the brick patchwork.

// --------------------------------------------------------------------------
// A wall face with rectangular holes.  axis "x": u runs along x, the wall's
// outer surface is the plane z = at, solid extends `out`-ward from at-WT to
// at.  axis "z": u runs along z, outer surface is x = at.
// Emits brick blocks around the holes plus a backing panel per hole at
// RECESS depth, so each opening is a niche, not a tunnel.
function emit(b, axis, uc, uw, y0, h, tA, tB, mat, opts = {}) {
  const tc = (tA + tB) / 2, tt = Math.abs(tB - tA);
  if (axis === "x") b.block(uc, y0, tc, uw, h, tt, mat, { ...FLAT, ...opts });
  else b.block(tc, y0, uc, tt, h, uw, mat, { ...FLAT, ...opts });
}

function wall(b, { axis, u0, u1, y0, y1, at, out, holes = [] }) {
  const inner = at - out * WT;
  const uniq = (vals, lo, hi) => {
    const s = [...vals].sort((p, q) => p - q), o = [];
    for (const v of s) {
      if (v < lo - 1e-4 || v > hi + 1e-4) continue;
      if (!o.length || v - o[o.length - 1] > 1e-4) o.push(v);
    }
    return o;
  };
  const hs = holes.map((r) => ({
    ...r,
    u0: Math.max(r.u0, u0), u1: Math.min(r.u1, u1),
    y0: Math.max(r.y0, y0), y1: Math.min(r.y1, y1),
  }));
  const us = uniq([u0, u1, ...hs.flatMap((r) => [r.u0, r.u1])], u0, u1);
  const ys = uniq([y0, y1, ...hs.flatMap((r) => [r.y0, r.y1])], y0, y1);
  const open = (uc, yc) =>
    hs.some((r) => uc > r.u0 + 1e-4 && uc < r.u1 - 1e-4 &&
                   yc > r.y0 + 1e-4 && yc < r.y1 - 1e-4);
  for (let j = 0; j < ys.length - 1; j++) {
    const ya = ys[j], yb = ys[j + 1], yc = (ya + yb) / 2;
    let run = null;                              // merge closed cells along u
    for (let i = 0; i < us.length; i++) {
      const closed = i < us.length - 1 && !open((us[i] + us[i + 1]) / 2, yc);
      if (closed && run === null) run = us[i];
      if (!closed && run !== null) {
        emit(b, axis, (run + us[i]) / 2, us[i] - run, ya, yb - ya, inner, at, B);
        run = null;
      }
    }
  }
  for (const r of hs) {                          // niche backing at RECESS
    if (r.through) continue;                     // a room behind it, not brick
    const back = at - out * (r.inset ?? RECESS);
    emit(b, axis, (r.u0 + r.u1) / 2, r.u1 - r.u0, r.y0, r.y1 - r.y0,
         inner, back, r.backMat ?? B);
  }
}

// Stone sill / lintel / keystone, proud of the wall face by `e`.
function dress(b, axis, at, out, r, kind) {
  const uc = (r.u0 + r.u1) / 2, uw = r.u1 - r.u0;
  const proud = (w, y, h, e, mat = ST) =>
    emit(b, axis, uc, w, y, h, at - out * 0.03, at + out * e, mat);
  if (kind === "sash" || kind === "arched")
    proud(uw + 0.14, r.y0 - 0.10, 0.10, 0.10);           // projecting sill
  if (kind === "sash" || kind === "door" || kind === "garage")
    proud(uw + 0.16, r.y1, 0.13, 0.08);                  // flat stone lintel
  if (kind === "arched")
    proud(0.24, r.y1 - 0.06, 0.34, 0.13);                // keystone at the crown
    // (only a 6 cm dip into the rect, so it cannot foul the arch surround
    // that openings.js hangs inside; the top loses itself in the soffit)
}

// --------------------------------------------------------------------------
// A hip roof: four slopes, ridge along the longer plan axis, built as six
// explicit triangles (two trapezoids as 2x2, two hip-end triangles), wound
// outward.  Built ridge-along-x locally then rotated when depth > width.
function hipRoof(b, cx, cz, w, d, yE, yR, mat, opts = {}) {
  const rot = d > w;
  const hw = (rot ? d : w) / 2, hd = (rot ? w : d) / 2, rise = yR - yE;
  const e0 = [-hw, 0, -hd], e1 = [hw, 0, -hd], e2 = [hw, 0, hd], e3 = [-hw, 0, hd];
  const r1 = [-hw + hd, rise, 0], r2 = [hw - hd, rise, 0];
  const tri = [];
  const P = (...pts) => tri.push(...pts.flat());
  P(e3, e2, r2); P(e3, r2, r1);       // long slope, +z side
  P(e1, e0, r1); P(e1, r1, r2);       // long slope, -z side
  P(e2, e1, r2);                      // hip end, +x
  P(e0, e3, r1);                      // hip end, -x
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(tri, 3));
  g.computeVertexNormals();
  if (rot) g.rotateY(Math.PI / 2);
  g.translate(cx, yE, cz);
  b.add(g, mat, { tile: 1.1, ...opts });
}

// A monopitch (shed) slab whose TOP surface runs (z0,y0) -> (z1,y1).
function shedRoof(b, xc, w, z0, y0, z1, y1, t, mat, opts = {}) {
  const a = Math.atan2(y0 - y1, z1 - z0);
  const g = new THREE.BoxGeometry(w, t, Math.hypot(z1 - z0, y0 - y1));
  g.rotateX(a);
  g.translate(xc, (y0 + y1) / 2 - (t / 2) * Math.cos(a),
              (z0 + z1) / 2 - (t / 2) * Math.sin(a));
  b.add(g, mat, { tile: opts.tile ?? 1.1, ...opts });
}

// A thin panel standing on edge from (x0,z0) to (x1,z1) -- the bay cheeks.
function slantPanel(b, x0, z0, x1, z1, y, h, t, mat) {
  const g = new THREE.BoxGeometry(Math.hypot(x1 - x0, z1 - z0), h, t);
  g.rotateY(Math.atan2(-(z1 - z0), x1 - x0));
  g.translate((x0 + x1) / 2, y + h / 2, (z0 + z1) / 2);
  b.add(g, mat, { ao: 0.05, tint: 0.05 });
}

// Sunburst fan: half-disc backing, nine radiating spokes, hub, base board.
// Faces +z; z is the backing plane (put it ~0.01 proud of the tympanum).
function sunburst(b, cx, cy, z, r) {
  const back = new THREE.CircleGeometry(r, 14, 0, Math.PI);
  back.translate(cx, cy, z);
  b.add(back, TS, { ao: 0, tint: 0.04 });
  for (let i = 0; i <= 8; i++) {                 // 9 spokes, both horizontals in
    const s = new THREE.BoxGeometry(r * 0.98, 0.05, 0.035);
    s.translate(r * 0.5, 0, 0);
    s.rotateZ((i / 8) * Math.PI);
    s.translate(cx, cy, z + 0.022);
    b.add(s, TR, { ao: 0, tint: 0.04 });
  }
  const hub = new THREE.CircleGeometry(r * 0.24, 10, 0, Math.PI);
  hub.translate(cx, cy, z + 0.042);
  b.add(hub, TR, { ao: 0, tint: 0.04 });
  b.block(cx, cy - 0.10, z + 0.005, r * 2 + 0.14, 0.10, 0.07, TR,
          { solid: false, ao: 0 });
}

// Raking trim boards along both edges of a front-facing gable.
function rakes(b, cx, yE, yApex, halfW, z, d) {
  const a = Math.atan2(yApex - yE, halfW);
  for (const s of [-1, 1]) {
    const g = new THREE.BoxGeometry(Math.hypot(halfW, yApex - yE) + 0.12, 0.10, d);
    g.rotateZ(-s * a);
    g.translate(cx + (s * halfW) / 2, (yE + yApex) / 2 + 0.03, z);
    b.add(g, TR, { ao: 0 });
  }
}

/**
 * A ground-floor window on a wall interior.js lines from the other side.
 *
 * The hole goes right through, so unlike fakeWindow the pane has to close it:
 * a frame round the reveal, glass lapped under it, and a muntin cross.  It
 * still sits at the old pane depth, which keeps the brick reveal the
 * elevation reads from outside.
 */
function throughWindow(b, w, r) {
  const uc = (r.u0 + r.u1) / 2, uw = r.u1 - r.u0, h = r.y1 - r.y0;
  const f0 = w.at - w.out * 0.055, f1 = w.at - w.out * 0.095;   // frame slab
  const g0 = w.at - w.out * 0.062, g1 = w.at - w.out * 0.080;   // glass
  const m0 = w.at - w.out * 0.080, m1 = w.at - w.out * 0.104;   // muntins
  const fw = 0.07;
  const soft = { ao: 0.02, tint: 0.04 };
  emit(b, w.axis, uc, uw, r.y0, fw, f0, f1, TR, soft);
  emit(b, w.axis, uc, uw, r.y1 - fw, fw, f0, f1, TR, soft);
  for (const sg of [-1, 1])
    emit(b, w.axis, uc + sg * (uw - fw) / 2, fw, r.y0 + fw, h - 2 * fw,
         f0, f1, TR, soft);
  emit(b, w.axis, uc, uw - 2 * fw + 0.01, r.y0 + fw - 0.005, h - 2 * fw + 0.01,
       g0, g1, GL, { ao: 0, tint: 0.04 });
  emit(b, w.axis, uc, 0.03, r.y0 + fw, h - 2 * fw, m0, m1, TR, soft);
  emit(b, w.axis, uc, uw - 2 * fw, (r.y0 + r.y1) / 2 - 0.015, 0.03,
       m0, m1, TR, soft);
  dress(b, w.axis, w.at, w.out, r, "sash");
}

// Blind window for the side/back walls (which H.OPENINGS does not cover):
// dark glass pane recessed in the niche, plus the stone dressing.
function fakeWindow(b, w, r) {
  const uc = (r.u0 + r.u1) / 2;
  emit(b, w.axis, uc, r.u1 - r.u0 - 0.16, r.y0 + 0.08, r.y1 - r.y0 - 0.16,
       w.at - w.out * 0.077, w.at - w.out * 0.047, GL, { ao: 0, tint: 0.04 });
  dress(b, w.axis, w.at, w.out, r, "sash");
}

// ==========================================================================
export function build(b) {
  const { Y, X, FACE, BACK, PORCH, ENTRY, BAY } = H;
  const L = X.left, R = X.right;

  // ---- the four walls ----------------------------------------------------
  const frontHoles = H.OPENINGS.map((o) => ({
    u0: o.x - o.w / 2, u1: o.x + o.w / 2,
    // the garage recess runs down to grade; a 5 cm brick sliver under the
    // door would read as a mistake and the driveway meets it anyway
    y0: o.y - o.h / 2 < 0.1 ? 0 : o.y - o.h / 2,
    y1: o.y + o.h / 2,
    through: o.through,
  }));
  wall(b, { axis: "x", u0: L, u1: R, y0: 0, y1: Y.eave,
            at: FACE, out: 1, holes: frontHoles });

  const backW = { axis: "x", u0: L, u1: R, y0: 0, y1: Y.eave,
                  at: BACK, out: -1 };
  const leftW = { axis: "z", u0: BACK + WT, u1: FACE - WT, y0: 0, y1: Y.eave,
                  at: L, out: -1 };
  const rightW = { axis: "z", u0: BACK + WT, u1: FACE - WT, y0: 0, y1: Y.eave,
                   at: R, out: 1 };
  // side/back fenestration is not in the spec, so it is shell dressing:
  // trim-backed niches with a dark pane, never touched by openings.js.
  // Every one of them comes back `through` from the spec now -- there is a
  // room on the other side of all of them, upstairs as well as down, and
  // openings.js's rule applies here too: a blind niche in a wall you can
  // stand behind reads as a mistake.  fakeWindow below is therefore unused
  // by the shipped spec and stays anyway: it is the other half of the
  // `through` contract, and the moment a wall gains a window with nothing
  // behind it -- a gable, a cupboard -- it is what fills it.
  const dressed = (r) => ({ ...r, backMat: TR, inset: 0.105 });
  const BD = H.BACK_DOOR;
  const backDoor = { u0: BD.x - BD.w / 2, u1: BD.x + BD.w / 2,
                     y0: BD.sill, y1: BD.head, through: true };
  const backWins = H.sideWindows("back").map(dressed);
  const leftWins = H.sideWindows("left").map(dressed);
  const rightWins = H.sideWindows("right").map(dressed);
  wall(b, { ...backW, holes: [...backWins, backDoor] });
  wall(b, { ...leftW, holes: leftWins });
  wall(b, { ...rightW, holes: rightWins });
  for (const r of backWins) (r.through ? throughWindow : fakeWindow)(b, backW, r);
  for (const r of leftWins) (r.through ? throughWindow : fakeWindow)(b, leftW, r);
  for (const r of rightWins) (r.through ? throughWindow : fakeWindow)(b, rightW, r);
  dress(b, "x", BACK, -1, backDoor, "door");     // stone lintel over the slider

  // Coarse colliders, one run per wall: the cat cannot thread a recess, and
  // the niches stay visually open without ever becoming pass-throughs.  The
  // two doorways are the exception -- the front door and the patio slider are
  // split around, or the house would be a sealed box with a door painted on.
  const fd = H.opening("frontDoor");
  const fdL = fd.x - fd.w / 2, fdR = fd.x + fd.w / 2, fdTop = fd.y + fd.h / 2;
  b.collider((L + fdL) / 2, 0, FACE - WT / 2, fdL - L, Y.eave, WT);
  b.collider((fdR + R) / 2, 0, FACE - WT / 2, R - fdR, Y.eave, WT);
  b.collider(fd.x, fdTop, FACE - WT / 2, fd.w, Y.eave - fdTop, WT);
  // Under the front door the masonry is solid but the wall runs stop either
  // side of it, so the threshold needs its own floor -- without this the
  // doorway is a 0.55 m slot between the porch slab and the hall.
  b.collider(fd.x, 0, FACE - WT / 2, fd.w, H.INSIDE.floor, WT);
  b.collider((L + backDoor.u0) / 2, 0, BACK + WT / 2, backDoor.u0 - L, Y.eave, WT);
  b.collider((backDoor.u1 + R) / 2, 0, BACK + WT / 2, R - backDoor.u1, Y.eave, WT);
  b.collider(BD.x, 0, BACK + WT / 2, BD.w, BD.sill, WT);          // under the sill
  b.collider(BD.x, BD.head, BACK + WT / 2, BD.w, Y.eave - BD.head, WT);
  b.collider(L + WT / 2, 0, (FACE + BACK) / 2, WT, Y.eave, FACE - BACK);
  b.collider(R - WT / 2, 0, (FACE + BACK) / 2, WT, Y.eave, FACE - BACK);

  // ---- stone dressings on the photographed openings -----------------------
  for (const o of H.OPENINGS) {
    if (o.kind === "bay") continue;              // the bay box carries its own
    dress(b, "x", FACE, 1,
          { u0: o.x - o.w / 2, u1: o.x + o.w / 2,
            y0: o.y - o.h / 2, y1: o.y + o.h / 2 }, o.kind);
  }

  // ---- foundation ----------------------------------------------------------
  /*
   * The walls start at grade, and this house's grade is its own bench: level
   * with the front elevation, while the ground under it ramps away to
   * GRADE.drop below by the back wall (spec.terraceY).  The other two houses
   * carry a 0.55 m brick plinth and simply extend it downward; this one has
   * no plinth to extend -- LOOK.brick starts at 0 and Y.plinth is only the
   * proud course below -- so it gets an underbuild instead: brick, top at
   * grade, underside 0.06 past the toe of the ramp so its edge is buried
   * rather than coplanar with the lawn.
   *
   * Solid, deliberately.  Every wall collider in this file starts at 0, so
   * with the retaining wall gone from ground.js there is a 0.80 m slot under
   * the back wall that a cat would walk straight through into the crawl
   * space.  This is what closes it.
   *
   * Sides flush with the plinth course's outer face; the back stops on the
   * wall plane so it stays clear of the deck framing butted against it.
   */
  const FOOT = -GRADE.drop - 0.06;
  b.block(0, FOOT, (BACK + FACE) / 2, R - L + 0.16, -FOOT, FACE - BACK, B,
          { tint: 0.05 });

  // ---- proud plinth course -------------------------------------------------
  // Skipped across the porch span (the deck top is also at 0.55 -- coplanar
  // tops would shimmer) and across the garage recess.
  const plinth = (x, z, w, d) =>
    b.block(x, 0, z, w, Y.plinth, d, B, { solid: false, tint: 0.05 });
  plinth((L + PORCH.minX) / 2, 1.51, PORCH.minX - L, 0.14);
  plinth((PORCH.maxX + 2.0) / 2, 1.51, 2.0 - PORCH.maxX, 0.14);
  plinth((5.30 + R) / 2, 1.51, R - 5.30, 0.14);
  plinth(L - 0.015, -5.0, 0.13, 12.88);          // sides wrap the corners
  plinth(R + 0.015, -5.0, 0.13, 12.88);
  plinth(0, BACK - 0.01, R - L, 0.13);

  // ---- main hip roof -------------------------------------------------------
  const eW = (R - L) + 2 * OV, eD = (FACE - BACK) + 2 * OV, zc = (FACE + BACK) / 2;
  hipRoof(b, 0, zc, eW, eD, Y.eave, Y.ridge, RF);
  // terraced colliders up the slopes: 0.50 m risers (jumpable, cat clears
  // 0.95) whose inset matches the 2.5/6.2 pitch, so the cat tracks the slope
  const pitch = (Y.ridge - Y.eave) / (eW / 2);
  for (let i = 0; i < 5; i++) {
    const inset = (0.5 / pitch) * i;
    // the outermost plate starts at 6.55 not 6.30: the eave overhangs the bay
    // cap (top 6.05) and a 6.30 underside would leave the 0.24 m cat only
    // 0.25 m of headroom while it stands there
    const y0 = i === 0 ? Y.eave + 0.25 : Y.eave;
    b.collider(0, y0, zc, eW - 2 * inset, Y.eave + 0.5 * (i + 1) - y0,
               eD - 2 * inset);
  }
  // fascia + soffit + gutters, all cream
  const fs = { solid: false, ao: 0 };
  b.block(0, Y.eave - 0.24, FACE + OV, eW + 0.12, 0.28, 0.12, TR, fs);
  b.block(0, Y.eave - 0.24, BACK - OV, eW + 0.12, 0.28, 0.12, TR, fs);
  b.block(L - OV, Y.eave - 0.24, zc, 0.12, 0.28, eD - 0.12, TR, fs);
  b.block(R + OV, Y.eave - 0.24, zc, 0.12, 0.28, eD - 0.12, TR, fs);
  /*
   * The soffit under the eaves -- a RING, not a sheet.
   *
   * It was one slab spanning the whole eave plan, which is 169 m2 of board
   * where the overhang is 0.45 m wide, and 90% of it hung inside the house
   * with nothing to hide it but the fact that nobody could get up there.
   * There is a storey up there now.  A downward face of `trim` takes the
   * hemisphere light's ground colour, so from every room on the first floor
   * the ceiling was a sheet of lawn green 0.13 m below its own plaster.
   *
   * Four bands round the wall line instead: the front and back run the full
   * width so the corners are covered once and only once, the sides fill
   * between them.  The outer edges land on eW/eD less the 0.12 the fascia
   * boards occupy, which is where they met before.
   *
   * `surface: false` because these are eaves.  A slab registers as ground
   * covering by default and the old one did; surfaceY ignores anything more
   * than 0.12 m off the earth so it never mattered, but four of them saying
   * the cat might stand at 6.20 m is four wrong answers waiting.
   */
  const so = { thickness: 0.03, surface: false };
  const soY = Y.eave - 0.13;
  const oX = (eW - 0.12) / 2, oZ0 = zc - (eD - 0.12) / 2, oZ1 = zc + (eD - 0.12) / 2;
  b.slab(0, soY, (FACE + oZ1) / 2, 2 * oX, oZ1 - FACE, TR, so);
  b.slab(0, soY, (BACK + oZ0) / 2, 2 * oX, BACK - oZ0, TR, so);
  b.slab((L - oX) / 2, soY, zc, oX + L, FACE - BACK, TR, so);
  b.slab((R + oX) / 2, soY, zc, oX - R, FACE - BACK, TR, so);
  b.bar(0, Y.eave - 0.20, FACE + OV + 0.11, 0.06, eW + 0.1, "x", TR, fs);
  b.bar(0, Y.eave - 0.20, BACK - OV - 0.11, 0.06, eW + 0.1, "x", TR, fs);
  b.bar(L - OV - 0.11, Y.eave - 0.20, zc, 0.06, eD, "z", TR, fs);
  b.bar(R + OV + 0.11, Y.eave - 0.20, zc, 0.06, eD, "z", TR, fs);
  // downspouts hug the four corners; short elbows reach out to the gutters
  for (const [sx, z] of [[-1, FACE - 0.20], [1, FACE - 0.20],
                         [-1, BACK + 0.20], [1, BACK + 0.20]]) {
    const x = sx * (R + 0.08);
    b.post(x, 0.02, z, 0.045, Y.eave - 0.20, TR, { ...fs, segments: 8 });
    b.bar(sx * (R + 0.30), Y.eave - 0.26, z, 0.04, 0.55, "x", TR,
          { ...fs, segments: 8 });
  }

  // ---- gable dormer with sunburst -----------------------------------------
  // Front face flush with the facade (a wall dormer); apex tops the main
  // ridge, so the prism's rear gable simply dies into the back of the slope
  // where the front slope reaches the dormer eave (z = -3.13).
  const DE = 8.35;                               // dormer eave height
  const dBack = FACE + OV - (DE - Y.eave) / pitch;
  const dw = 2.3, dx = X.dormer;
  b.block(dx, Y.eave - 0.02, FACE - 0.09, dw, DE - Y.eave + 0.02, 0.18, B, FLAT);
  b.block(dx - dw / 2 + 0.09, Y.eave - 0.02, (dBack + FACE) / 2, 0.18,
          DE - Y.eave + 0.02, FACE - dBack, B, FLAT);
  b.block(dx + dw / 2 - 0.09, Y.eave - 0.02, (dBack + FACE) / 2, 0.18,
          DE - Y.eave + 0.02, FACE - dBack, B, FLAT);
  b.roof(dx, DE, (dBack + FACE + 0.05) / 2, dw + 0.36, FACE + 0.05 - dBack,
         Y.dormerApex - DE, RF, { ridge: "z", tile: 1.1 });
  b.roof(dx, DE, FACE + 0.03, dw, 0.26, Y.dormerApex - DE, TR,
         { ridge: "z", solid: false, ao: 0 });   // trim tympanum, proud 0.11
  rakes(b, dx, DE, Y.dormerApex, dw / 2, FACE + 0.19, 0.08);
  sunburst(b, dx, 8.72, FACE + 0.17, 0.50);
  for (const s of [-1, 1])                       // eave fascia strips
    b.block(dx + s * (dw / 2 + 0.18), DE - 0.13, (dBack + FACE) / 2, 0.12,
            0.20, FACE - dBack, TR, fs);
  b.collider(dx, Y.eave, (dBack + FACE) / 2, dw, DE - Y.eave, FACE - dBack);

  // ---- entry gable with sunburst -------------------------------------------
  // A pediment over the steps, piercing the porch shed roof.  Its eave (3.55)
  // keeps the prism buried in the shed's thickness so no slab hangs into the
  // porch, and -- deliberately -- it stops short of the wall: at full apex it
  // would bury the sills of the upper window pair pinned by H.OPENINGS.
  const EG = { yE: 3.55, halfW: ENTRY.gableW / 2 + 0.15 };
  b.roof(X.door, EG.yE, (2.20 + 3.10) / 2, EG.halfW * 2, 0.90,
         Y.entryApex - EG.yE, RF, { ridge: "z", tile: 1.1 });
  b.roof(X.door, EG.yE + 0.03, ENTRY.gableFront + 0.01, ENTRY.gableW + 0.02,
         0.18, Y.entryApex - EG.yE - 0.06, TR, { ridge: "z", solid: false, ao: 0 });
  rakes(b, X.door, EG.yE, Y.entryApex, EG.halfW, ENTRY.gableFront + 0.11, 0.08);
  sunburst(b, X.door, 3.96, ENTRY.gableFront + 0.11, 0.42);

  // ---- porch roof (structure below is porch.js's) --------------------------
  // Shed from the wall plate (porchRidge) down to the eave past the deck edge.
  // Note: the spec pins porchRidge (3.95) above the arch rect's sill (3.75),
  // so the shed unavoidably laps the bottom 0.2 m of that opening at the wall.
  const P = { x0: -5.78, x1: 1.20, zOut: PORCH.front + 0.32 };
  const pxc = (P.x0 + P.x1) / 2, pw = P.x1 - P.x0;
  shedRoof(b, pxc, pw, FACE - 0.18, Y.porchRidge + 0.05, P.zOut, Y.porchEave, 0.14, RF);
  shedRoof(b, pxc, pw - 0.08, FACE - 0.18, Y.porchRidge - 0.08, P.zOut - 0.04,
           Y.porchEave - 0.13, 0.05, TR, { ao: 0 });   // soffit == porch ceiling
  shedRoof(b, P.x0 + 0.02, 0.12, FACE - 0.18, Y.porchRidge + 0.08, P.zOut + 0.04,
           Y.porchEave + 0.02, 0.20, TR, { ao: 0 });   // raking side fascia
  shedRoof(b, P.x1 - 0.02, 0.12, FACE - 0.18, Y.porchRidge + 0.08, P.zOut + 0.04,
           Y.porchEave + 0.02, 0.20, TR, { ao: 0 });
  b.block(pxc, Y.porchEave - 0.20, P.zOut + 0.06, pw, 0.28, 0.12, TR, fs);
  b.bar(pxc, Y.porchEave - 0.20, P.zOut + 0.17, 0.05, pw + 0.02, "x", TR, fs);
  // two flat terraces at the shed's real heights (surface runs 3.95 -> 3.30);
  // porch.js's railing is the way up, this just makes the roof standable
  b.collider(pxc, 3.00, (2.90 + P.zOut) / 2, pw, 0.44, P.zOut - 2.90);  // top 3.44
  b.collider(pxc, 3.20, (FACE + 2.90) / 2, pw, 0.61, 2.90 - FACE);      // top 3.81

  // ---- the bay-window projection over the garage ---------------------------
  // Splayed box: flat front panel BAY.project out, angled cheeks 0.55 back to
  // the wall.  openings.js glazes the front; the cheeks read as trim panels.
  const bx = BAY.centreX, bw = BAY.width / 2;                  // 2.0 .. 5.0
  const fz = FACE + BAY.project;                               // 2.28
  const fx = bw - BAY.cheek;                                   // front half-width
  const chH = BAY.head - BAY.sill;
  // floor: stone slab pieces with a projecting nose (tops offset 5 mm where
  // the rotated cheek slabs overlap the straight ones, so nothing is coplanar)
  b.block(bx, BAY.sill - 0.11, (FACE + 2.04) / 2, BAY.width - 0.1, 0.121,
          2.04 - FACE + 0.08, ST, fs);
  b.block(bx, BAY.sill - 0.105, (2.04 + fz + 0.08) / 2, 2 * fx + 0.24, 0.12,
          fz + 0.08 - 2.04, ST, fs);
  slantPanel(b, bx - bw - 0.06, FACE - 0.05, bx - fx + 0.04, fz + 0.05,
             BAY.sill - 0.115, 0.13, 0.34, ST);
  slantPanel(b, bx + bw + 0.06, FACE - 0.05, bx + fx - 0.04, fz + 0.05,
             BAY.sill - 0.115, 0.13, 0.34, ST);
  // corbelled underside
  b.block(bx, BAY.sill - 0.40, (FACE - 0.04 + 2.22) / 2, BAY.width - 0.16, 0.30,
          2.26 - FACE, TR, fs);
  b.block(bx, BAY.sill - 0.62, (FACE - 0.04 + 2.02) / 2, BAY.width - 0.50, 0.23,
          2.06 - FACE, TR, fs);
  // cheeks + corner posts + head band
  slantPanel(b, bx - bw - 0.04, FACE - 0.06, bx - fx + 0.03, fz + 0.04,
             BAY.sill, chH, 0.09, TR);
  slantPanel(b, bx + bw + 0.04, FACE - 0.06, bx + fx - 0.03, fz + 0.04,
             BAY.sill, chH, 0.09, TR);
  b.block(bx - fx, BAY.sill - 0.02, fz - 0.03, 0.12, chH + 0.04, 0.14, TR, fs);
  b.block(bx + fx, BAY.sill - 0.02, fz - 0.03, 0.12, chH + 0.04, 0.14, TR, fs);
  b.block(bx, BAY.head, fz - 0.02, 2 * fx + 0.10, 0.17, 0.11, TR, fs);
  slantPanel(b, bx - bw - 0.04, FACE - 0.06, bx - fx + 0.03, fz + 0.04,
             BAY.head, 0.17, 0.11, TR);
  slantPanel(b, bx + bw + 0.04, FACE - 0.06, bx + fx - 0.03, fz + 0.04,
             BAY.head, 0.17, 0.11, TR);
  // hipped cap: eave plan overhangs the box 0.12; the rear half of the hip is
  // buried inside the wall, which keeps the helper symmetric and seam-free
  hipRoof(b, bx, FACE - 0.06, BAY.width + 0.24, 2 * (BAY.project + 0.18),
          BAY.head + 0.17, BAY.capTop, RF);
  // colliders: the box itself, and a flat cap top at its true 6.05 m height
  // (a cat up here can clear 6.05 + 0.95 onto the lowest roof terrace at 6.80)
  b.collider(bx, BAY.sill - 0.62, (FACE + fz + 0.06) / 2, BAY.width + 0.12,
             BAY.head + 0.17 - BAY.sill + 0.62, fz + 0.06 - FACE);
  b.collider(bx, BAY.head + 0.17, (FACE + fz + 0.12) / 2, BAY.width + 0.24,
             BAY.capTop - BAY.head - 0.17, fz + 0.12 - FACE);

  // ---- roof furniture -------------------------------------------------------
  // satellite dish on the left wall: shallow cone, base facing up-and-out
  b.bar(L - 0.10, 5.34, -1.5, 0.03, 0.26, "x", MD, { ...fs, segments: 6 });
  const dish = new THREE.ConeGeometry(0.30, 0.10, 12);
  dish.rotateZ(-2.0);
  dish.rotateY(0.5);
  dish.translate(L - 0.24, 5.44, -1.45);
  b.add(dish, MP, { tile: 0, ao: 0 });
  // two vents on the rear slope (surface height = eave + run * pitch)
  b.post(2.6, 7.42, -8.6, 0.10, 0.55, MD, { ...fs, segments: 8 });
  b.block(2.6, 7.95, -8.6, 0.26, 0.06, 0.26, MD, fs);
  b.block(-3.4, 7.10, -9.4, 0.34, 0.40, 0.34, MD, fs);
  b.block(-3.4, 7.48, -9.4, 0.44, 0.06, 0.44, MD, fs);

  b.mark("shellPorchRoof", pxc, 3.81, 2.2);
  b.mark("shellBayCap", bx, BAY.capTop, FACE + 0.4);
  b.mark("shellRidge", 0, Y.ridge, zc);
}
